import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Save, X } from 'lucide-react';
import { Button } from '@components/Button';
import { Input } from '@components/Input';
import { Card, CardContent } from '@components/Card';
import { SimpleTable as Table, TableHeader, TableRow, TableCell } from '@components/Table';
import { PermissionGuard } from '@components/PermissionGuard';
import { acquirerService } from '@services/acquirerService';
import { Acquirer, AcquirerPIXTax, AcquirerPIXTaxFormData } from '@/types/acquirer';

// MinTax/MaxTax são tarifa em REAIS por transação (ex.: Itaú 0,50% com teto de R$ 4,99), não percentual.
const fmtBRL = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const emptyForm: AcquirerPIXTaxFormData = {
  tax_rate: 0,
  min_tax: null,
  min_installments: 1,
  max_installments: 1,
  ec: null,
  acquirer_id: 0,
  cnpj: '',
  max_tax: null,
};

export const AcquirerPIXTaxTab: React.FC = () => {
  const [items, setItems] = useState<AcquirerPIXTax[]>([]);
  const [acquirers, setAcquirers] = useState<Acquirer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [editingItem, setEditingItem] = useState<AcquirerPIXTax | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<AcquirerPIXTaxFormData>({ ...emptyForm });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const pageSize = 20;

  const loadLookups = async () => {
    try {
      const acqs = await acquirerService.listAllAcquirers();
      setAcquirers(acqs);
    } catch { /* silently ignore */ }
  };

  const loadItems = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await acquirerService.getPIXTaxes({ page: currentPage, pageSize });
      setItems(result.data);
      setTotalPages(result.pagination.totalPages);
      setTotalItems(result.pagination.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadLookups(); }, []);
  useEffect(() => { loadItems(); }, [currentPage]);

  const getAcquirerName = (id: number) => acquirers.find(a => a.id === id)?.name ?? `#${id}`;

  const openCreateForm = () => {
    setEditingItem(null);
    setFormData({ ...emptyForm });
    setFormErrors({});
    setShowForm(true);
  };

  const openEditForm = (item: AcquirerPIXTax) => {
    setEditingItem(item);
    setFormData({
      tax_rate: item.tax_rate,
      min_tax: item.min_tax,
      min_installments: item.min_installments,
      max_installments: item.max_installments,
      ec: item.ec,
      acquirer_id: item.acquirer_id,
      cnpj: item.cnpj ?? '',
      max_tax: item.max_tax,
    });
    setFormErrors({});
    setShowForm(true);
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (!formData.acquirer_id) errors.acquirer_id = 'Adquirente é obrigatório';
    if (formData.tax_rate < 0 || formData.tax_rate > 99.99) errors.tax_rate = 'Taxa deve ser entre 0 e 99.99';
    if (formData.min_installments < 1) errors.min_installments = 'Mínimo de parcelas deve ser >= 1';
    if (formData.max_installments < formData.min_installments) errors.max_installments = 'Máximo deve ser >= mínimo de parcelas';
    if (formData.cnpj && formData.cnpj.length > 14) errors.cnpj = 'CNPJ deve ter no máximo 14 caracteres';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    try {
      setSaving(true);
      setError(null);
      if (editingItem) {
        await acquirerService.updatePIXTax(editingItem.id, formData);
      } else {
        await acquirerService.createPIXTax(formData);
      }
      setShowForm(false);
      loadItems();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await acquirerService.deletePIXTax(id);
      setDeleteConfirm(null);
      loadItems();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir');
    }
  };

  const updateField = (field: keyof AcquirerPIXTaxFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) setFormErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-600">Taxas PIX por adquirente.</p>
        <PermissionGuard permission="receiving_hub.write">
          <Button size="sm" onClick={openCreateForm} icon={<Plus size={16} />}>Nova Taxa</Button>
        </PermissionGuard>
      </div>

      {error && (
        <Card className="bg-red-50 border-red-200">
          <CardContent><p className="text-red-600 text-sm">{error}</p></CardContent>
        </Card>
      )}

      {showForm && (
        <Card className="border-emerald-200 bg-emerald-50/30">
          <CardContent>
            <h3 className="text-sm font-semibold text-slate-700 mb-3">
              {editingItem ? 'Editar Taxa PIX' : 'Nova Taxa PIX'}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Adquirente <span className="text-red-500">*</span></label>
                <select className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" value={formData.acquirer_id} onChange={e => updateField('acquirer_id', Number(e.target.value))}>
                  <option value={0}>Selecione...</option>
                  {acquirers.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
                {formErrors.acquirer_id && <p className="text-xs text-red-500 mt-1">{formErrors.acquirer_id}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Taxa (%) <span className="text-red-500">*</span></label>
                <Input type="number" step="0.01" value={formData.tax_rate || ''} onChange={e => updateField('tax_rate', parseFloat(e.target.value) || 0)} placeholder="0.00" />
                {formErrors.tax_rate && <p className="text-xs text-red-500 mt-1">{formErrors.tax_rate}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Tarifa mínima (R$)</label>
                <Input type="number" step="0.01" value={formData.min_tax ?? ''} onChange={e => updateField('min_tax', e.target.value ? parseFloat(e.target.value) : null)} placeholder="0.00" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Tarifa máxima (R$)</label>
                <Input type="number" step="0.01" value={formData.max_tax ?? ''} onChange={e => updateField('max_tax', e.target.value ? parseFloat(e.target.value) : null)} placeholder="0.00" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Parcelas Mín. <span className="text-red-500">*</span></label>
                <Input type="number" value={formData.min_installments} onChange={e => updateField('min_installments', parseInt(e.target.value) || 1)} placeholder="1" />
                {formErrors.min_installments && <p className="text-xs text-red-500 mt-1">{formErrors.min_installments}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Parcelas Máx. <span className="text-red-500">*</span></label>
                <Input type="number" value={formData.max_installments} onChange={e => updateField('max_installments', parseInt(e.target.value) || 1)} placeholder="1" />
                {formErrors.max_installments && <p className="text-xs text-red-500 mt-1">{formErrors.max_installments}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">EC</label>
                <Input type="number" value={formData.ec ?? ''} onChange={e => updateField('ec', e.target.value ? Number(e.target.value) : null)} placeholder="Código EC" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">CNPJ</label>
                <Input value={formData.cnpj} onChange={e => updateField('cnpj', e.target.value)} placeholder="CNPJ (14 dígitos)" maxLength={14} />
                {formErrors.cnpj && <p className="text-xs text-red-500 mt-1">{formErrors.cnpj}</p>}
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" size="sm" onClick={() => setShowForm(false)} icon={<X size={14} />}>Cancelar</Button>
              <Button size="sm" onClick={handleSave} loading={saving} icon={<Save size={14} />}>{editingItem ? 'Salvar' : 'Criar'}</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <tr>
                <th className="text-left">ID</th>
                <th className="text-left">Adquirente</th>
                <th className="text-right">Taxa (%)</th>
                <th className="text-right">Tarifa mín. (R$)</th>
                <th className="text-right">Tarifa máx. (R$)</th>
                <th className="text-center">Parcelas</th>
                <th className="text-left">CNPJ</th>
                <th className="text-right">Ações</th>
              </tr>
            </TableHeader>
            <tbody>
              {loading ? (
                [...Array(3)].map((_, i) => (
                  <TableRow key={i}>
                    {[...Array(8)].map((_, j) => (
                      <TableCell key={j}><div className="h-4 bg-slate-200 rounded w-16 animate-pulse" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-slate-500 py-8">Nenhuma taxa encontrada</TableCell>
                </TableRow>
              ) : (
                items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="font-mono text-sm">{item.id}</TableCell>
                    <TableCell>{getAcquirerName(item.acquirer_id)}</TableCell>
                    <TableCell className="text-right font-mono">{item.tax_rate.toFixed(2)}%</TableCell>
                    <TableCell className="text-right font-mono">{item.min_tax != null ? fmtBRL(item.min_tax) : '—'}</TableCell>
                    <TableCell className="text-right font-mono">{item.max_tax != null ? fmtBRL(item.max_tax) : '—'}</TableCell>
                    <TableCell className="text-center text-sm">{item.min_installments}x — {item.max_installments}x</TableCell>
                    <TableCell className="text-sm text-slate-600">{item.cnpj ?? '—'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <PermissionGuard permission="receiving_hub.write">
                          <Button variant="secondary" size="sm" onClick={() => openEditForm(item)} icon={<Edit size={14} />}>Editar</Button>
                        </PermissionGuard>
                        <PermissionGuard permission="receiving_hub.write">
                          {deleteConfirm === item.id ? (
                            <div className="flex items-center gap-1">
                              <Button variant="danger" size="sm" onClick={() => handleDelete(item.id)}>Confirmar</Button>
                              <Button variant="secondary" size="sm" onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
                            </div>
                          ) : (
                            <Button variant="outline" size="sm" onClick={() => setDeleteConfirm(item.id)} icon={<Trash2 size={14} />}>Excluir</Button>
                          )}
                        </PermissionGuard>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </tbody>
          </Table>
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-600">Total: {totalItems} registro(s)</p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>Anterior</Button>
            <span className="text-sm text-slate-600">Página {currentPage} de {totalPages}</span>
            <Button variant="outline" size="sm" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>Próxima</Button>
          </div>
        </div>
      )}
    </div>
  );
};
